import React from 'react';
import { TABLET_MIN_WIDTH } from '../../../utils';
import Slider, { Settings } from 'react-slick';
import { CompareState, Notes } from '../../types';
import { FareGroupsCompareTableQuery } from '@websky/graphql';
import { ApolloError } from 'apollo-boost';
import Header from './Header/Header';
import Content from './Content/Content';
import Footer from './Footer/Footer';

interface Props {
	headerRef: React.RefObject<HTMLDivElement>;
	compareTable: FareGroupsCompareTableQuery;
	notes: Notes;
	isLoading?: boolean;
	selectedFare?: string;
	selectedFareName?: string;
	error?: ApolloError;
}

const FaresSlider: React.FC<Props> = ({
	headerRef,
	isLoading,
	compareTable,
	selectedFare,
	selectedFareName,
	error,
	notes
}) => {
	const topSliderRef = React.useRef<Slider>();
	const contentTableRef: React.RefObject<HTMLDivElement> = React.useRef();

	const [currentSlide, setCurrentSlide] = React.useState(0);
	const [compareState, onCompareStateChange] = React.useState(CompareState.CompareFares);

	const sliders: Slider[] = [];
	let compareFaresSlidesCount, fullCompareFaresSlidesCount: number;

	const isFullFareConditions = compareState === CompareState.FullFareConditions;

	if (!isLoading && !error) {
		const fareGroupsCount = compareTable?.FareGroupsCompareTable.header.length;
		compareFaresSlidesCount = fareGroupsCount < 5 ? fareGroupsCount : 5;
		fullCompareFaresSlidesCount = fareGroupsCount < 3 ? fareGroupsCount : 3;
	}

	const resetSlidersIndex = React.useCallback(() => {
		topSliderRef.current?.slickGoTo(0);
		sliders.forEach(slider => slider.slickGoTo(0));
		setCurrentSlide(0);
	}, [topSliderRef, sliders, currentSlide]);

	const handleCompareStateChange: React.MouseEventHandler<HTMLLinkElement> = e => {
		e.preventDefault();
		resetSlidersIndex();
		headerRef?.current?.scrollIntoView({ behavior: 'smooth' });
		onCompareStateChange(isFullFareConditions ? CompareState.CompareFares : CompareState.FullFareConditions);
	};

	React.useEffect(() => {
		sliders.forEach(slider => slider.slickGoTo(currentSlide));

		if (topSliderRef.current) {
			topSliderRef.current.slickGoTo(currentSlide);
		}
	}, [currentSlide, topSliderRef]);

	const sliderSettings: Settings = {
		arrows: false,
		speed: 250,
		swipe: false,
		slidesToShow: isFullFareConditions ? fullCompareFaresSlidesCount : compareFaresSlidesCount,
		infinite: false,
		initialSlide: currentSlide,
		responsive: [
			{
				breakpoint: TABLET_MIN_WIDTH,
				settings: {
					slidesToShow: 1,
					slidesToScroll: 1
				}
			}
		]
	};

	return (
		<>
			<div id="content__table" ref={contentTableRef}>
				<Header
					onSetCurrentSlide={setCurrentSlide}
					settings={sliderSettings}
					compareTableHeader={compareTable?.FareGroupsCompareTable.header}
					isFullFareConditions={isFullFareConditions}
					selectedFare={selectedFare}
					selectedFareName={selectedFareName}
					contentTableRef={contentTableRef}
					topSliderRef={topSliderRef}
				/>
				<Content
					sliders={sliders}
					settings={sliderSettings}
					compareTableGroups={compareTable?.FareGroupsCompareTable.groups}
					isFullFareConditions={isFullFareConditions}
				/>
			</div>

			<Footer
				notes={notes}
				isFullFareConditions={isFullFareConditions}
				isLoading={isLoading}
				error={error}
				onLinkClick={handleCompareStateChange}
			/>
		</>
	);
};

export default FaresSlider;
